import { useCallback } from "react"
import type { MutableRefObject } from "react"
import type { QueryClient } from "@tanstack/react-query"
import type { AxiosResponse } from "axios"
import { applyUpdatedErefsToCache, removeEcallFromCache } from "helpers/quotationCache.helper"
import { enqueueMutation } from "store/mutationQueue.store"
import AnnotationService from "services/annotations.service"
import { ecallQueueKey } from "../EcallSidebar/ecallQueueKey"
import type { useDeleteEcall } from "hooks/api/quotation/quotations.hook"
import type { TEcall, TEcallDeleteResponse } from "types/elevationReferences.type"
import type { TQuotationDetail } from "types/quotations.type"

// REQ-297 ecall delete, raised from the ecall sidebar's ⋯ menu. Deleting an elevation callout is
// NOT a cascade: the backend detaches the erefs its elevations pointed at and returns them with
// bumped versions, so the cache has to move those versions forward alongside dropping the ecall —
// otherwise the next edit of a detached eref would send a stale version and fail the optimistic-lock
// check.
//
// The request goes through the ecall's own mutation queue so it is serialized against the other
// writer on that key — the coordinate-drag commits in `useCoordinateCommit`. Note the ecall SIDEBAR
// is NOT on this queue: unlike the eref and furniture sidebars (which use `useEntityMutationQueue`),
// it fires its PATCHes directly and tracks the version in a local ref, so a delete CAN still race a
// field or elevation-row commit the user started a moment earlier. That race costs a spurious error
// toast behind the success one, not a corrupt cache — the ecall row is already gone, and splicing a
// late response into a missing row is a no-op. Putting the sidebar on the queue would close it.
// The endpoint itself takes no `version` — delete is not version-checked — so nothing is chained out.
export type EcallDeleteContext = {
	numericQuotationIdRef: MutableRefObject<number | undefined>
	setDeleteConfirm: (value: null) => void
	setBatchPending: (value: boolean) => void
	deleteEcallAsync: ReturnType<typeof useDeleteEcall>["mutateAsync"]
	/** Post-delete cleanup owned by the orchestrator: close the ecall sidebar / split view. */
	onEcallDeleted: (ecallId: number) => void
	queryClient: QueryClient
	success: (text: string) => void
	formatError: (error: unknown) => void
}

export const useEcallDelete = (ctx: EcallDeleteContext) => {
	const {
		numericQuotationIdRef,
		setDeleteConfirm,
		setBatchPending,
		deleteEcallAsync,
		onEcallDeleted,
		queryClient,
		success,
		formatError,
	} = ctx

	const runEcallDelete = useCallback(
		async (ecall: TEcall) => {
			const qid = numericQuotationIdRef.current
			if (qid == null || Number.isNaN(qid)) return
			setBatchPending(true)
			// Failures are handled INSIDE the queued task (as the ecall sidebar's mutations do), so the
			// queue's promise chain never sees a rejection.
			await enqueueMutation(ecallQueueKey(ecall.id), ecall.version, async () => {
				try {
					const resp = (await deleteEcallAsync({ quotationId: qid, ecallId: ecall.id }))?.data as
						| TEcallDeleteResponse
						| undefined
					const updatedErefs = resp?.updated_erefs ?? []

					queryClient.setQueryData<AxiosResponse<TQuotationDetail> | undefined>(["quotation", qid], (cached) => {
						// Bumps the detached erefs' cached versions, forward-only, so a later edit of one of
						// them doesn't fail the optimistic-lock check.
						const next = applyUpdatedErefsToCache(cached, updatedErefs)
						return removeEcallFromCache(next, ecall.id)
					})

					AnnotationService.removeEcallAnnotation(ecall.id)

					onEcallDeleted(ecall.id)
					AnnotationService.deselectAll()
					setDeleteConfirm(null)
					success("Elevation callout deleted")
				} catch (e) {
					formatError(e)
					queryClient.invalidateQueries({ queryKey: ["quotation", qid] })
				} finally {
					setBatchPending(false)
				}
			})
		},
		[
			numericQuotationIdRef,
			setDeleteConfirm,
			setBatchPending,
			deleteEcallAsync,
			onEcallDeleted,
			queryClient,
			success,
			formatError,
		],
	)

	return { runEcallDelete }
}
